// src/store/conversationSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axiosInstance from '../utils/axiosConfig';
import { logout } from './authSlice'

// News combining helpers removed; news agents are now separate streams

// Legacy helper no longer used now that news agents are split

// const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// 1. Fetch the user's conversation list
export const fetchConversations = createAsyncThunk(
  'conversation/fetchConversations',
  async ({ user_id, pageNum = 1, limit = 25 }, { rejectWithValue }) => {
    try {
      const resp = await axiosInstance.get(`/conversation/listAll`, {
        params: { user_id, pageNum, limit },
      });
      if (resp.status !== 200) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      // console.log('Fetched conversations:', resp.data.conversations);
      return {
        conversations: resp.data.conversations,
        pagination: resp.data.pagination
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// 2. Create a new conversation
export const createConversation = createAsyncThunk(
  'conversation/createConversation',
  async ({ user_id, title }, { rejectWithValue }) => {
    try {
      const body = {
        user_id,
        title: title || 'Untitled Chat',
      }
      const resp = await axiosInstance.post(`/conversation/create`, body)
      if (resp.status !== 200) {
        return rejectWithValue(`Error: ${resp.status}`)
      }
      
      // Transform the response to include the title we sent
      return {
        ...resp.data,
        title: title || 'Untitled Chat',  // Ensure we use the title we sent
        updated_at: new Date().toISOString()  // Add timestamp for sorting
      };
    } catch (error) {
      console.log('Error creating conversation:', error);
      console.log('Error message:', error.message);
      return rejectWithValue(error.message)
    }
  }
)

// 3. Update conversation title
export const updateConversation = createAsyncThunk(
  'conversation/updateConversation',
  async ({ conversation_id, title }, { rejectWithValue }) => {
    try {
      // console.log('Updating conversation:', { conversation_id, title });
      const body = { conversation_id, title };
      const resp = await axiosInstance.put(`/conversation/update`, body);
      // console.log('Update response:', resp.data);
      
      if (resp.status !== 200) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      return { conversation_id, title };
    } catch (error) {
      console.error('Update error:', error);
      return rejectWithValue(error.message);
    }
  }
)

// 4. Delete conversation
export const deleteConversation = createAsyncThunk(
  'conversation/deleteConversation',
  async (conversation_id, { rejectWithValue }) => {
    try {
      // console.log('Deleting conversation:', conversation_id);
      const body = { conversation_id };
      const resp = await axiosInstance.delete(`/conversation/delete`, { data: body });
      // console.log('Delete response:', resp.data);
      
      if (resp.status !== 200) {
        return rejectWithValue(`Error: ${resp.status}`);
      }
      return { conversation_id };
    } catch (error) {
      console.error('Delete error:', error);
      return rejectWithValue(error.message);
    }
  }
)

// 5. Fetch messages for both agents
export const fetchMessages = createAsyncThunk(
  'conversation/fetchMessages',
  async ({ conversation_id, pageNum = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      // console.log('Fetching dual-agent messages for conversation:', conversation_id, 'page:', pageNum);
      
      // Call all agent endpoints in parallel
      const [onChainResult, newsResult, twitterResult, sentimentResult, quantPredictiveResult, quantTechnicianResult, startResult] = await Promise.all([
        axiosInstance.get(`/message/listAllOnChain`, {
          params: { conversation_id, pageNum, limit },
        }).catch(error => {
          console.log('On-chain messages not found:', error.message);
          return { data: { messages: [], pagination: null } };
        }),
        
        axiosInstance.get(`/message/listAllNews`, {
          params: { conversation_id, pageNum, limit },
        }).catch(error => {
          console.error('News messages API error:', error.response ?? error);
          return { data: { messages: [], pagination: null } };
        }),

        axiosInstance.get(`/message/listAllTwitter`, {
          params: { conversation_id, pageNum, limit },
        }).catch(error => {
          console.error('Twitter messages API error:', error.response ?? error);
          return { data: { messages: [], pagination: null } };
        }),

        axiosInstance.get(`/message/listAllSentiment`, {
          params: { conversation_id, pageNum, limit },
        }).catch(error => {
          console.error('Sentiment messages API error:', error.response ?? error);
          return { data: { messages: [], pagination: null } };
        }),

        axiosInstance.get(`/message/listAllQuantPredictive`, {
          params: { conversation_id, pageNum, limit },
        }).catch(error => {
          console.error('Quant Predictive messages API error:', error.response ?? error);
          return { data: { messages: [], pagination: null } };
        }),

        axiosInstance.get(`/message/listAllTechnician`, {
          params: { conversation_id, pageNum, limit },
        }).catch(error => {
          console.error('Quant Technician messages API error:', error.response ?? error);
          return { data: { messages: [], pagination: null } };
        }),

        axiosInstance.get(`/message/listAllVesterStart`, {
          params: { conversation_id, pageNum, limit },
        }).catch(error => {
          console.error('Vester Start messages API error:', error.response ?? error);
          return { data: { messages: [], pagination: null } };
        })
      ]);
      /*
      console.log('Fetched on-chain data:', onChainResult.data);
      console.log('Fetched news data:', newsResult.data);
      console.log('Fetched twitter data:', twitterResult.data);
      console.log('Fetched sentiment data:', sentimentResult.data);
      console.log('Fetched quant predictive data:', quantPredictiveResult.data);
      console.log('Fetched quant technician data:', quantTechnicianResult.data);
      try { console.log('Fetched vester start data:', startResult.data); } catch (e) {}
      */
      // Extract and reverse messages (newest first)
      const onChainMessages = onChainResult.data.onChainMessages ? [...onChainResult.data.onChainMessages].reverse() : [];
      const newsMessagesRaw = newsResult.data.newsMessages ? [...newsResult.data.newsMessages].reverse() : [];
      const twitterMessagesRaw = twitterResult.data.sentimentMessages ? [...twitterResult.data.sentimentMessages].reverse() : [];
      const sentimentMessagesRaw = sentimentResult.data.sentimentMessages ? [...sentimentResult.data.sentimentMessages].reverse() : [];
      const quantPredictiveMessages = quantPredictiveResult.data.quantPredictiveMessages ? [...quantPredictiveResult.data.quantPredictiveMessages].reverse() : [];
      const quantTechnicianMessages = quantTechnicianResult.data.technicianMessages ? [...quantTechnicianResult.data.technicianMessages].reverse() : [];
      // Support multiple possible keys from backend for Start agent messages
      let startMessages = [];
      if (Array.isArray(startResult?.data?.startMessages)) {
        startMessages = [...startResult.data.startMessages].reverse();
      } else if (Array.isArray(startResult?.data?.messages)) {
        startMessages = [...startResult.data.messages].reverse();
      } else if (Array.isArray(startResult?.data?.vesterStartMessages)) {
        startMessages = [...startResult.data.vesterStartMessages].reverse();
      }

      // Keep news agents split into their own streams (no combining)

      return { 
        conversation_id, 
        onChainMessages,
        newsMessages: newsMessagesRaw,
        twitterMessages: twitterMessagesRaw,
        sentimentMessages: sentimentMessagesRaw,
        quantPredictiveMessages,
        quantTechnicianMessages,
        startMessages,
        pagination: onChainResult.data.pagination || { currentPage: pageNum, totalPages: 1, hasMore: false },
        isFirstPage: pageNum === 1
      }
    } catch (error) {
      console.log('Error fetching messages:', error);
      return rejectWithValue(error.message)
    }
  }
)

// 5b. Fetch ONLY multiagent outputs (do not fetch/replace Start messages)
export const fetchAgentOutputs = createAsyncThunk(
  'conversation/fetchAgentOutputs',
  async ({ conversation_id, pageNum = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      // Call all agent endpoints in parallel (exclude Start)
      const [onChainResult, newsResult, twitterResult, sentimentResult, quantPredictiveResult, quantTechnicianResult] = await Promise.all([
        axiosInstance.get(`/message/listAllOnChain`, {
          params: { conversation_id, pageNum, limit },
        }).catch(() => ({ data: { messages: [], pagination: null } })),

        axiosInstance.get(`/message/listAllNews`, {
          params: { conversation_id, pageNum, limit },
        }).catch(() => ({ data: { messages: [], pagination: null } })),

        axiosInstance.get(`/message/listAllTwitter`, {
          params: { conversation_id, pageNum, limit },
        }).catch(() => ({ data: { messages: [], pagination: null } })),

        axiosInstance.get(`/message/listAllSentiment`, {
          params: { conversation_id, pageNum, limit },
        }).catch(() => ({ data: { messages: [], pagination: null } })),

        axiosInstance.get(`/message/listAllQuantPredictive`, {
          params: { conversation_id, pageNum, limit },
        }).catch(() => ({ data: { messages: [], pagination: null } })),

        axiosInstance.get(`/message/listAllTechnician`, {
          params: { conversation_id, pageNum, limit },
        }).catch(() => ({ data: { messages: [], pagination: null } })),
      ]);

      // Extract and reverse messages (newest first)
      const onChainMessages = onChainResult.data.onChainMessages ? [...onChainResult.data.onChainMessages].reverse() : [];
      const newsMessagesRaw = newsResult.data.newsMessages ? [...newsResult.data.newsMessages].reverse() : [];
      const twitterMessagesRaw = twitterResult.data.sentimentMessages ? [...twitterResult.data.sentimentMessages].reverse() : [];
      const sentimentMessagesRaw = sentimentResult.data.sentimentMessages ? [...sentimentResult.data.sentimentMessages].reverse() : [];
      const quantPredictiveMessages = quantPredictiveResult.data.quantPredictiveMessages ? [...quantPredictiveResult.data.quantPredictiveMessages].reverse() : [];
      const quantTechnicianMessages = quantTechnicianResult.data.technicianMessages ? [...quantTechnicianResult.data.technicianMessages].reverse() : [];

      return {
        conversation_id,
        onChainMessages,
        newsMessages: newsMessagesRaw,
        twitterMessages: twitterMessagesRaw,
        sentimentMessages: sentimentMessagesRaw,
        quantPredictiveMessages,
        quantTechnicianMessages,
        pagination: onChainResult.data.pagination || { currentPage: pageNum, totalPages: 1, hasMore: false },
        isFirstPage: pageNum === 1,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
)

// 6. Fetch deep agent messages for a conversation
export const fetchDeepAgentMessages = createAsyncThunk(
  'conversation/fetchDeepAgentMessages',
  async ({ conversation_id, pageNum = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const resp = await axiosInstance.get(`/message/listAllDeepagent`, {
        params: { conversation_id, pageNum, limit },
      });

      if (resp.status !== 200) {
        return rejectWithValue(`Error: ${resp.status}`);
      }

      // Extract messages and reverse (newest first)
      const deepAgentMessages = resp.data.vesterStartMessages
        ? [...resp.data.vesterStartMessages].reverse()
        : [];

      // console.log('Fetched deep agent messages:', deepAgentMessages);

      return {
        conversation_id,
        deepAgentMessages,
        pagination: resp.data.pagination || { currentPage: pageNum, totalPages: 1, hasMore: false },
        isFirstPage: pageNum === 1
      };
    } catch (error) {
      console.error('Error fetching deep agent messages:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Fetch raw deep agent messages from backend (no transform, no reverse)
// Useful when we need to resolve backend chart_id for a streamed visual that has a temporary client id.
export const fetchDeepAgentMessagesRaw = createAsyncThunk(
  'conversation/fetchDeepAgentMessagesRaw',
  async ({ conversation_id, pageNum = 1, limit = 200 }, { rejectWithValue }) => {
    try {
      const resp = await axiosInstance.get(`/message/listAllDeepagent`, {
        params: { conversation_id, pageNum, limit },
      });

      if (resp.status !== 200) {
        return rejectWithValue(`Error: ${resp.status}`);
      }

      // API typically returns oldest-first; we intentionally keep the order as-is.
      const rawMessages =
        resp.data?.vesterStartMessages ||
        resp.data?.deepAgentMessages ||
        resp.data?.messages ||
        [];

      return {
        conversation_id,
        rawMessages,
        pagination: resp.data.pagination || { currentPage: pageNum, totalPages: 1, hasMore: false },
        isFirstPage: pageNum === 1,
      };
    } catch (error) {
      console.error('Error fetching raw deep agent messages:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Fetch only the last deep agent message from backend (after streaming completes)
export const fetchLastDeepAgentMessage = createAsyncThunk(
  'conversation/fetchLastDeepAgentMessage',
  async ({ conversation_id }, { rejectWithValue }) => {
    try {
      // Fetch all messages for this conversation (or a large limit to ensure we get the latest)
      // The API returns messages oldest-first, so we need to get all and take the last one
      const resp = await axiosInstance.get(`/message/listAllDeepagent`, {
        params: { conversation_id, pageNum: 1, limit: 100 },
      });

      if (resp.status !== 200) {
        return rejectWithValue(`Error: ${resp.status}`);
      }

      // Extract the most recent message (last in the array since API returns oldest-first)
      const messages = resp.data.vesterStartMessages || [];

      if (messages.length === 0) {
        return rejectWithValue('No messages found');
      }

      // Get the last message (most recent) from the array
      const lastMessage = messages[messages.length - 1];


      return {
        conversation_id,
        lastMessage,
      };
    } catch (error) {
      console.error('Error fetching last deep agent message:', error);
      return rejectWithValue(error.message);
    }
  }
);

// More complex sort function if needed
const sortConversations = (conversations) => {
  return conversations.sort((a, b) => {
    const timeCompare = new Date(b.updated_at) - new Date(a.updated_at);
    if (timeCompare === 0) {
      // If timestamps are equal, sort by title
      return (a.title || '').localeCompare(b.title || '');
    }
    return timeCompare;
  });
};

const conversationSlice = createSlice({
  name: 'conversation',
  initialState: {
    conversationList: [],
    activeConversation: null, // { conversation_id, title, startMessages: [...], onChainMessages: [...], newsMessages: [...], quantPredictiveMessages: [...], deepAgentMessages: [...] }
    loading: false,
    error: null,
    pagination: {
      conversations: {
        currentPage: 1,
        totalPages: 1,
        hasMore: false,
      },
      messages: {
        currentPage: 1,
        totalPages: 1,
        hasMore: false,
      },
    }
  },

  reducers: {
    setActiveConversation: (state, action) => {
      state.activeConversation = action.payload;
      // Reset message pagination when switching conversation
      state.pagination.messages = {
        currentPage: 1,
        totalPages: 1,
        hasMore: false,
      };
    },
    // Start Agent: add user query to startMessages list
    addStartUserQuery: (state, action) => {
      const { conversationId, query } = action.payload;
      if (state.activeConversation?.conversation_id === conversationId) {
        if (!Array.isArray(state.activeConversation.startMessages)) {
          state.activeConversation.startMessages = [];
        }
        state.activeConversation.startMessages.push({
          query,
          response: null,
          message_id: null,
          needs_multiagent: null,
          visualization_html: null,
          explanation_paragraphs: [],
          resources: [],
          recommended_questions: [],
          has_multiagent_output: false,
        });
      }
    },
    // Start Agent: attach parsed agent response to the last start message
    addStartAgentResponse: (state, action) => {
      const { conversationId, payload } = action.payload || {};
      if (state.activeConversation?.conversation_id !== conversationId) return;
      if (!Array.isArray(state.activeConversation.startMessages) || state.activeConversation.startMessages.length === 0) return;
      const lastIndex = state.activeConversation.startMessages.length - 1;
      const prev = state.activeConversation.startMessages[lastIndex] || {};
      state.activeConversation.startMessages[lastIndex] = {
        ...prev,
        response: true,
        message_id: payload?.message_id ?? prev.message_id ?? null,
        needs_multiagent: payload?.needs_multiagent ?? null,
        visualization_html: payload?.visualization_html ?? null,
        explanation_paragraphs: Array.isArray(payload?.explanation_paragraphs) ? payload.explanation_paragraphs : [],
        resources: Array.isArray(payload?.resources) ? payload.resources : [],
        recommended_questions: Array.isArray(payload?.recommended_questions) ? payload.recommended_questions : [],
      };
    },
    // Mark a start message (last) as having multiagent output available
    markLastStartMessageHasMultiagent: (state, action) => {
      const { conversationId } = action.payload || {};
      if (state.activeConversation?.conversation_id !== conversationId) return;
      if (!Array.isArray(state.activeConversation.startMessages) || state.activeConversation.startMessages.length === 0) return;
      const lastIndex = state.activeConversation.startMessages.length - 1;
      const prev = state.activeConversation.startMessages[lastIndex] || {};
      state.activeConversation.startMessages[lastIndex] = {
        ...prev,
        has_multiagent_output: true,
      };
    },
    // Add user query to both agent arrays for optimistic UI
    addUserQuery: (state, action) => {
      const { conversationId, query } = action.payload;
      if (state.activeConversation?.conversation_id === conversationId) {
        // Add to both arrays with placeholder responses
        if (!state.activeConversation.startMessages) {
          state.activeConversation.startMessages = [];
        }
        if (!state.activeConversation.onChainMessages) {
          state.activeConversation.onChainMessages = [];
        }
        if (!state.activeConversation.newsMessages) {
          state.activeConversation.newsMessages = [];
        }
        if (!state.activeConversation.twitterMessages) {
          state.activeConversation.twitterMessages = [];
        }
        if (!state.activeConversation.sentimentMessages) {
          state.activeConversation.sentimentMessages = [];
        }
        if (!state.activeConversation.quantPredictiveMessages) {
          state.activeConversation.quantPredictiveMessages = [];
        }
        if (!state.activeConversation.quantTechnicianMessages) {
          state.activeConversation.quantTechnicianMessages = [];
        }
        
        // Add query to both arrays
        state.activeConversation.onChainMessages.push({
          query,
          visualization_details: [{ chart_code: null, key_data_observations: null }]
        });
        // News (analyst) placeholder
        state.activeConversation.newsMessages.push({
          query,
          visualization_details: [{ chart_code: null }],
          news_analysis: { institutional_analysis: '', market_impact: '', regulatory_impact: '' }
        });
        // Twitter placeholder (inline analysis will come from sentiment stream per spec)
        state.activeConversation.twitterMessages.push({
          query,
          twitter_analysis: ''
        });
        // Sentiment placeholder (provides chart for twitter/sql)
        state.activeConversation.sentimentMessages.push({
          query,
          visualization_details: [{ chart_code: null }]
        });
        // Quant Predictive placeholder (chart_code base64-encoded upstream)
        state.activeConversation.quantPredictiveMessages.push({
          query,
          predictive_sql_query_execution: null,
          predictive_analytics: null,
          predictive_visualizations: [{ chart_code: null }],
          forecasting_analysis: null,
        });
        // Quant Technician placeholder
        state.activeConversation.quantTechnicianMessages.push({
          query,
          descriptive_sql_query_execution: null,
          descriptive_analytics: null,
          descriptive_visualizations: [{ chart_code: null }]
        });
      }
    },
    // Update agent responses (called when streaming completes)
    addModelResponse: (state, action) => {
      const { conversationId, agent, responseData } = action.payload;
      if (state.activeConversation?.conversation_id === conversationId) {
        let targetArray = 'onChainMessages';
        if (agent === 'news') targetArray = 'newsMessages';
        if (agent === 'twitter') targetArray = 'twitterMessages';
        if (agent === 'sentiment') targetArray = 'sentimentMessages';
        if (agent === 'quantPredictive') targetArray = 'quantPredictiveMessages';
        if (agent === 'quantTechnician') targetArray = 'quantTechnicianMessages';
        if (!Array.isArray(state.activeConversation[targetArray])) {
          state.activeConversation[targetArray] = [];
        }
        const lastIndex = state.activeConversation[targetArray].length - 1;
        
        if (lastIndex >= 0) {
          // Update the last message with the streaming response
          state.activeConversation[targetArray][lastIndex] = {
            ...state.activeConversation[targetArray][lastIndex],
            ...responseData
          };
        } else {
          // If no optimistic item exists, push a new one safely
          state.activeConversation[targetArray].push({ ...responseData });
        }
      }
    },
    // Revert the last optimistic user query added when a stream is cancelled
    revertLastUserQuery: (state, action) => {
      const { conversationId } = action.payload || {};
      if (state.activeConversation?.conversation_id !== conversationId) return;
      if (Array.isArray(state.activeConversation.onChainMessages) && state.activeConversation.onChainMessages.length > 0) {
        state.activeConversation.onChainMessages.pop();
      }
      if (Array.isArray(state.activeConversation.newsMessages) && state.activeConversation.newsMessages.length > 0) {
        state.activeConversation.newsMessages.pop();
      }
      if (Array.isArray(state.activeConversation.twitterMessages) && state.activeConversation.twitterMessages.length > 0) {
        state.activeConversation.twitterMessages.pop();
      }
      if (Array.isArray(state.activeConversation.sentimentMessages) && state.activeConversation.sentimentMessages.length > 0) {
        state.activeConversation.sentimentMessages.pop();
      }
      if (Array.isArray(state.activeConversation.quantPredictiveMessages) && state.activeConversation.quantPredictiveMessages.length > 0) {
        state.activeConversation.quantPredictiveMessages.pop();
      }
    },
    clearActiveConversation: (state) => {
      state.activeConversation = null;
      // Reset message pagination
      state.pagination.messages = {
        currentPage: 1,
        totalPages: 1,
        hasMore: false,
      };
    },
    // Deep Agent: add user query to deepAgentMessages
    addDeepAgentUserQuery: (state, action) => {
      const { conversationId, query } = action.payload;
      if (state.activeConversation?.conversation_id === conversationId) {
        if (!Array.isArray(state.activeConversation.deepAgentMessages)) {
          state.activeConversation.deepAgentMessages = [];
        }
        state.activeConversation.deepAgentMessages.push({
          messageId: null,
          query,
          analysisPlan: [],
          visuals: [],
          textContent: '',
          streamSections: [],
          isComplete: false,
          isStreaming: true,
          raw: '',
          created_at: new Date().toISOString(),
        });
      }
    },
    // Deep Agent: update streaming message with parsed data
    updateDeepAgentMessage: (state, action) => {
      const { conversationId, messageData } = action.payload;
      if (state.activeConversation?.conversation_id === conversationId) {
        if (!Array.isArray(state.activeConversation.deepAgentMessages)) {
          state.activeConversation.deepAgentMessages = [];
        }
        const lastIndex = state.activeConversation.deepAgentMessages.length - 1;
        if (lastIndex >= 0) {
          // Check if any visual has 'dashboard' in filename (isFinal flag)
          const hasOverviewChart = messageData.visuals?.some(visual => visual.isFinal) || false;

          state.activeConversation.deepAgentMessages[lastIndex] = {
            ...state.activeConversation.deepAgentMessages[lastIndex],
            ...messageData,
            hasOverviewChart, // Set flag for dashboard charts during streaming
          };
        }
      }
    },
    // Deep Agent: mark message as complete
    completeDeepAgentMessage: (state, action) => {
      const { conversationId } = action.payload;
      if (state.activeConversation?.conversation_id === conversationId) {
        if (!Array.isArray(state.activeConversation.deepAgentMessages)) return;
        const lastIndex = state.activeConversation.deepAgentMessages.length - 1;
        if (lastIndex >= 0) {
          state.activeConversation.deepAgentMessages[lastIndex].isComplete = true;
          state.activeConversation.deepAgentMessages[lastIndex].isStreaming = false;
        }
      }
    },
    // Deep Agent: pause/interrupt the current stream (stop streaming UI and show a pause row)
    pauseDeepAgentMessage: (state, action) => {
      const { conversationId, pauseType, pauseMessage } = action.payload || {};
      if (state.activeConversation?.conversation_id !== conversationId) return;
      if (!Array.isArray(state.activeConversation.deepAgentMessages)) return;
      const lastIndex = state.activeConversation.deepAgentMessages.length - 1;
      if (lastIndex < 0) return;

      const prev = state.activeConversation.deepAgentMessages[lastIndex] || {};
      state.activeConversation.deepAgentMessages[lastIndex] = {
        ...prev,
        isStreaming: false,
        // Keep isComplete as-is (some flows mark complete even on early disconnect).
        streamPause: {
          type: pauseType || 'interrupted', // 'cancelled' | 'interrupted'
          message: pauseMessage || null,
          at: new Date().toISOString(),
        },
      };
    },
    // Deep Agent: replace last message with backend data (after streaming completes)
    replaceLastDeepAgentMessage: (state, action) => {
      const { conversationId, transformedMessage } = action.payload;
      if (state.activeConversation?.conversation_id === conversationId) {
        if (!Array.isArray(state.activeConversation.deepAgentMessages)) return;
        const lastIndex = state.activeConversation.deepAgentMessages.length - 1;
        if (lastIndex >= 0) {
          // Replace the last message with the backend-fetched version
          state.activeConversation.deepAgentMessages[lastIndex] = transformedMessage;
        }
      }
    },
    // Deep Agent: revert last user query if stream cancelled
    revertLastDeepAgentQuery: (state, action) => {
      const { conversationId } = action.payload;
      if (state.activeConversation?.conversation_id === conversationId) {
        if (Array.isArray(state.activeConversation.deepAgentMessages) && state.activeConversation.deepAgentMessages.length > 0) {
          state.activeConversation.deepAgentMessages.pop();
        }
      }
    },
  },
  extraReducers: (builder) => {
    // fetchConversations
    builder.addCase(fetchConversations.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchConversations.fulfilled, (state, action) => {
      state.loading = false;
      
      const { conversations, pagination } = action.payload;
      
      // If it's the first page, replace the list
      if (pagination.currentPage === 1) {
        state.conversationList = sortConversations(conversations);
      } else {
        // Otherwise, append to the existing list
        const newList = [...state.conversationList, ...conversations];
        state.conversationList = sortConversations(newList);
      }
      
      // Update pagination info
      state.pagination.conversations = {
        currentPage: pagination.currentPage,
        totalPages: pagination.totalPages,
        hasMore: pagination.currentPage < pagination.totalPages
      };
    });
    builder.addCase(fetchConversations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    
    // createConversation
    builder.addCase(createConversation.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createConversation.fulfilled, (state, action) => {
      state.loading = false;
      // Add new conversation to the beginning of the list
      state.conversationList.unshift(action.payload);
    });
    builder.addCase(createConversation.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    
    // updateConversation
    builder.addCase(updateConversation.fulfilled, (state, action) => {
      const { conversation_id, title } = action.payload;
      const found = state.conversationList.find(c => c.conversation_id === conversation_id);
      if (found) {
        found.title = title;
        found.updated_at = new Date().toISOString();
        // Re-sort the list
        state.conversationList = state.conversationList.sort((a, b) => 
          new Date(b.updated_at) - new Date(a.updated_at)
        );
      }
      if (state.activeConversation?.conversation_id === conversation_id) {
        state.activeConversation.title = title;
      }
    });
    
    // deleteConversation
    builder.addCase(deleteConversation.fulfilled, (state, action) => {
      const { conversation_id } = action.payload;
      state.conversationList = state.conversationList.filter(
        (c) => c.conversation_id !== conversation_id
      );
      if (state.activeConversation?.conversation_id === conversation_id) {
        state.activeConversation = null;
      }
    });
    
    // fetchMessages (dual-agent)
    builder.addCase(fetchMessages.fulfilled, (state, action) => {
      const { conversation_id, onChainMessages, newsMessages, twitterMessages, sentimentMessages, quantPredictiveMessages, quantTechnicianMessages, startMessages, pagination, isFirstPage } = action.payload;
      
      if (state.activeConversation?.conversation_id === conversation_id) {
        // If it's the first page, replace the messages
        if (isFirstPage) {
          state.activeConversation = {
            ...state.activeConversation,
            startMessages: startMessages,
            onChainMessages: onChainMessages,
            newsMessages: newsMessages,
            twitterMessages: twitterMessages || [],
            sentimentMessages: sentimentMessages || [],
            quantPredictiveMessages: quantPredictiveMessages,
            quantTechnicianMessages: quantTechnicianMessages
          };
        } else {
          // For subsequent pages, prepend older messages
          state.activeConversation = {
            ...state.activeConversation,
            startMessages: [...(startMessages || []), ...(state.activeConversation.startMessages || [])],
            onChainMessages: [...onChainMessages, ...(state.activeConversation.onChainMessages || [])],
            newsMessages: [...newsMessages, ...(state.activeConversation.newsMessages || [])],
            twitterMessages: [...(twitterMessages || []), ...(state.activeConversation.twitterMessages || [])],
            sentimentMessages: [...(sentimentMessages || []), ...(state.activeConversation.sentimentMessages || [])],
            quantPredictiveMessages: [...(quantPredictiveMessages || []), ...(state.activeConversation.quantPredictiveMessages || [])],
            quantTechnicianMessages: [...(quantTechnicianMessages || []), ...(state.activeConversation.quantTechnicianMessages || [])]
          };
        }
        
        // Update pagination info
        state.pagination.messages = {
          currentPage: pagination.currentPage,
          totalPages: pagination.totalPages,
          hasMore: pagination.currentPage < pagination.totalPages
        };
      }
    });

    // fetchAgentOutputs (updates only agent arrays; preserves startMessages)
    builder.addCase(fetchAgentOutputs.fulfilled, (state, action) => {
      const { conversation_id, onChainMessages, newsMessages, twitterMessages, sentimentMessages, quantPredictiveMessages, quantTechnicianMessages, pagination, isFirstPage } = action.payload;
      if (state.activeConversation?.conversation_id === conversation_id) {
        if (isFirstPage) {
          state.activeConversation = {
            ...state.activeConversation,
            onChainMessages: onChainMessages,
            newsMessages: newsMessages,
            twitterMessages: twitterMessages || [],
            sentimentMessages: sentimentMessages || [],
            quantPredictiveMessages: quantPredictiveMessages,
            quantTechnicianMessages: quantTechnicianMessages,
          };
        } else {
          state.activeConversation = {
            ...state.activeConversation,
            onChainMessages: [...onChainMessages, ...(state.activeConversation.onChainMessages || [])],
            newsMessages: [...newsMessages, ...(state.activeConversation.newsMessages || [])],
            twitterMessages: [...(twitterMessages || []), ...(state.activeConversation.twitterMessages || [])],
            sentimentMessages: [...(sentimentMessages || []), ...(state.activeConversation.sentimentMessages || [])],
            quantPredictiveMessages: [...(quantPredictiveMessages || []), ...(state.activeConversation.quantPredictiveMessages || [])],
            quantTechnicianMessages: [...(quantTechnicianMessages || []), ...(state.activeConversation.quantTechnicianMessages || [])],
          };
        }
        // Update pagination info
        state.pagination.messages = {
          currentPage: pagination.currentPage,
          totalPages: pagination.totalPages,
          hasMore: pagination.currentPage < pagination.totalPages,
        };
      }
    });
    
    builder.addCase(logout, (state) => {
      state.activeConversation = null;
      state.conversationList = [];
      // Reset pagination
      state.pagination = {
        conversations: {
          currentPage: 1,
          totalPages: 1,
          hasMore: false,
        },
        messages: {
          currentPage: 1,
          totalPages: 1,
          hasMore: false,
        },
      };
    });

    // fetchDeepAgentMessages
    builder.addCase(fetchDeepAgentMessages.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchDeepAgentMessages.fulfilled, (state, action) => {
      state.loading = false;
      const { conversation_id, deepAgentMessages, pagination, isFirstPage } = action.payload;

      if (state.activeConversation?.conversation_id === conversation_id) {
        if (isFirstPage) {
          state.activeConversation = {
            ...state.activeConversation,
            deepAgentMessages: deepAgentMessages,
          };
        } else {
          // For subsequent pages, prepend older messages
          state.activeConversation = {
            ...state.activeConversation,
            deepAgentMessages: [...deepAgentMessages, ...(state.activeConversation.deepAgentMessages || [])],
          };
        }

        // Update pagination info
        state.pagination.messages = {
          currentPage: pagination.currentPage,
          totalPages: pagination.totalPages,
          hasMore: pagination.currentPage < pagination.totalPages
        };
      }
    });
    builder.addCase(fetchDeepAgentMessages.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // fetchLastDeepAgentMessage (doesn't set loading state to avoid UI flicker)
    builder.addCase(fetchLastDeepAgentMessage.fulfilled, (_state, _action) => {
      // This is handled via replaceLastDeepAgentMessage action in the component
      // The component will transform the message and dispatch replaceLastDeepAgentMessage
    });
    builder.addCase(fetchLastDeepAgentMessage.rejected, (_state, action) => {
      console.error('Failed to fetch last message from backend:', action.payload);
      // Don't show error to user - streaming data is already there
    });
  },
});

export const {
  setActiveConversation,
  addStartUserQuery,
  addStartAgentResponse,
  markLastStartMessageHasMultiagent,
  addUserQuery,
  addModelResponse,
  revertLastUserQuery,
  clearActiveConversation,
  addDeepAgentUserQuery,
  updateDeepAgentMessage,
  completeDeepAgentMessage,
  pauseDeepAgentMessage,
  replaceLastDeepAgentMessage,
  revertLastDeepAgentQuery,
} = conversationSlice.actions;

export default conversationSlice.reducer;
